import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/useAuth";
import {
  HomeIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CreditCardIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
} from "./icons";
import logoInversionesGiampier from "../assets/logo-inversiones-giampier.png";
// Simple class name utility
const cn = (...classes: (string | undefined | null | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

const navigation = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: HomeIcon,
    roles: ["admin", "client", "operator"],
  },
  // Sección para Clientes
  {
    name: "Nueva Efectivización",
    href: "/solicitar",
    icon: CurrencyDollarIcon,
    roles: ["client"],
  },
  {
    name: "Mis Solicitudes",
    href: "/my-transactions",
    icon: ClockIcon,
    roles: ["client"],
  },
  {
    name: "Mi Perfil",
    href: "/onboarding",
    icon: UserIcon,
    roles: ["client"],
  },
  {
    name: "Cuenta Bancaria",
    href: "/bank-accounts",
    icon: CreditCardIcon,
    roles: ["client"],
  },
  // Sección para Administradores
  {
    name: "Gestionar Solicitudes",
    href: "/transactions",
    icon: CurrencyDollarIcon,
    roles: ["admin", "operator"],
  },
  {
    name: "Gestionar Usuarios",
    href: "/users",
    icon: UserIcon,
    roles: ["admin"],
  },
  {
    name: "Panel Admin",
    href: "/admin",
    icon: Cog6ToothIcon,
    roles: ["admin"],
  },
  // Sección compartida
  {
    name: "Configuración",
    href: "/settings",
    icon: Cog6ToothIcon,
    roles: ["admin", "client"],
  },
];

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const filteredNavigation = navigation.filter((item) =>
    item.roles.some((role) => hasRole(role)),
  );

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div
        className={cn(
          "fixed inset-0 z-50 lg:hidden",
          sidebarOpen ? "block" : "hidden",
        )}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white shadow-xl">
          <div className="flex h-20 items-center justify-between px-4 border-b border-gray-200">
            <div className="flex items-center">
              <img
                src={logoInversionesGiampier}
                alt="Inversiones Giampier"
                className="h-14 w-auto object-contain"
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <button
                  key={item.name}
                  onClick={() => {
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                  className={cn(
                    "group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium",
                    isActive
                      ? "bg-blue-100 text-blue-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  )}
                >
                  <item.icon
                    className={cn(
                      "mr-3 h-5 w-5 flex-shrink-0",
                      isActive
                        ? "text-blue-500"
                        : "text-gray-400 group-hover:text-gray-500",
                    )}
                  />
                  {item.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-20 items-center justify-center px-4 border-b border-gray-200">
            <img
              src={logoInversionesGiampier}
              alt="Inversiones Giampier"
              className="h-16 w-auto object-contain"
            />
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className={cn(
                    "group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium",
                    isActive
                      ? "bg-blue-100 text-blue-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  )}
                >
                  <item.icon
                    className={cn(
                      "mr-3 h-5 w-5 flex-shrink-0",
                      isActive
                        ? "text-blue-500"
                        : "text-gray-400 group-hover:text-gray-500",
                    )}
                  />
                  {item.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* User info */}
              <div className="flex items-center gap-x-2">
                <div className="text-sm">
                  <p className="font-medium text-gray-900">{user?.name}</p>
                  <p className="text-gray-500">
                    {user?.roles.map((role) => role.name).join(", ")}
                  </p>
                </div>
              </div>

              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="flex items-center gap-x-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-4">
          <div className="mx-auto max-w-7xl px-4 sm:px-5 lg:px-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
